import React, { useState, useEffect } from 'react';
import { CONFIG } from '../config';

export default function AITestPage() {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [channel, setChannel] = useState('sms');
  const [messageBody, setMessageBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [conversation, setConversation] = useState([]);

  useEffect(() => {
    Promise.all([
      fetch(`${CONFIG.apiBaseUrl}/active-event`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${CONFIG.apiBaseUrl}/admin/events`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([activeData, eventsData]) => {
      const evts = eventsData.events || [];
      setEvents(evts);
      const active = activeData.activeEventId || (evts[0]?.event_id ?? '');
      setSelectedEventId(active);
    }).catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!messageBody.trim() || !selectedEventId) return;

    const userMessage = { role: 'user', channel, text: messageBody.trim() };
    setConversation(prev => [...prev, userMessage]);
    setMessageBody('');
    setSubmitting(true);
    setError(null);

    try {
      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/test-ai-response`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message_body: userMessage.text,
          channel,
          event_id: selectedEventId,
        }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        throw new Error(data.error || 'Request failed');
      }

      setConversation(prev => [...prev, {
        role: 'bot',
        channel,
        text: data.response_text || '(no response)',
        intent: data.intent,
        action_taken: data.action_taken,
        source: data.source,
        truncated: data.truncated,
      }]);
    } catch (err) {
      setError(err.message);
      setConversation(prev => [...prev, { role: 'error', text: err.message }]);
    } finally {
      setSubmitting(false);
    }
  };

  const clearConversation = () => setConversation([]);

  const sourceLabel = (source) => {
    if (source === 'bedrock_agent') return 'Bedrock Agent';
    if (source === 'lex_intent') return 'Lex Intent Handler';
    if (source === 'lex') return 'Lex';
    return source || '—';
  };

  return (
    <div className="whatsapp-responses-page">
      <h2 className="page-title">AI Response Tester</h2>
      <p className="page-subtitle">
        Simulate inbound messages and preview AI responses without sending anything to guests.
      </p>

      <div className="settings-section">
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <div className="form-group" style={{ flex: '1', minWidth: '200px', margin: 0 }}>
            <label className="form-label">Event</label>
            {loading ? (
              <div className="loading-message" style={{ fontSize: '0.9em' }}>Loading...</div>
            ) : (
              <select
                className="form-input"
                value={selectedEventId}
                onChange={(e) => { setSelectedEventId(e.target.value); clearConversation(); }}
              >
                {events.map(ev => (
                  <option key={ev.event_id} value={ev.event_id}>{ev.event_name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group" style={{ flex: '1', minWidth: '160px', margin: 0 }}>
            <label className="form-label">Channel</label>
            <select
              className="form-input"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
            >
              <option value="sms">SMS</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>

          {conversation.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={clearConversation}>
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Conversation thread */}
        {conversation.length > 0 && (
          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
            maxHeight: '480px',
            overflowY: 'auto',
            background: '#f9fafb',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}>
            {conversation.map((msg, i) => {
              if (msg.role === 'error') {
                return (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <span style={{ color: '#dc3545', fontSize: '0.85em' }}>Error: {msg.text}</span>
                  </div>
                );
              }

              const isUser = msg.role === 'user';
              return (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '75%',
                    padding: '10px 14px',
                    borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    background: isUser
                      ? (msg.channel === 'whatsapp' ? '#25D366' : '#007acc')
                      : '#ffffff',
                    color: isUser ? '#fff' : '#1a1a1a',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    fontSize: '0.95em',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                    border: isUser ? 'none' : '1px solid #e5e7eb',
                  }}>
                    {msg.text}
                  </div>
                  {!isUser && (
                    <div style={{ fontSize: '0.75em', color: '#6b7280', marginTop: '4px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <span>Source: {sourceLabel(msg.source)}</span>
                      {msg.intent && <span>Intent: {msg.intent}</span>}
                      {msg.action_taken && <span>Action: {msg.action_taken}</span>}
                      {msg.truncated && <span style={{ color: '#b45309' }}>✂ truncated to 500 chars</span>}
                    </div>
                  )}
                </div>
              );
            })}
            {submitting && (
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '18px 18px 18px 4px',
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  color: '#6b7280',
                  fontSize: '0.9em',
                }}>
                  Thinking...
                </div>
              </div>
            )}
          </div>
        )}

        {/* Input form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <textarea
              className="form-input"
              rows={3}
              value={messageBody}
              onChange={(e) => setMessageBody(e.target.value)}
              placeholder={`Type a test message as if a guest sent it via ${channel === 'whatsapp' ? 'WhatsApp' : 'SMS'}...`}
              disabled={submitting || loading}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              style={{ resize: 'vertical', marginBottom: 0 }}
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || !messageBody.trim() || !selectedEventId || loading}
            style={{ whiteSpace: 'nowrap' }}
          >
            {submitting ? 'Sending...' : 'Send'}
          </button>
        </form>
        <div className="input-hint" style={{ marginTop: '6px' }}>
          Press Enter to send, Shift+Enter for a new line. Responses are not delivered to any guest.
        </div>
      </div>
    </div>
  );
}
